import dayjs from "dayjs";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router-dom";
import Tooltip from "../../components/ui/tooltip/Tooltip";
import ModalPdfContracts from "../../components/modalPdfContracts/ModalPdfContracts";
import SidebarAddContractDocument from "../../components/sidebarAddContractDocument/sidebarAddContractDocument";
import { AgGridReact } from "ag-grid-react";
import { IContractApi } from "../../types/contracts";
import { getContractsStatusIcon } from "../../utils/helpers/statusIcons";
import { contractsService } from "../../api/contractsService/contractsService";
import { toast } from "react-toastify";
import InputText from "../../components/ui/inputText/InputText";
import Spinner from "../../components/ui/loaders/spinner/Spinner";
import Button from "../../components/ui/button/Button";
import { ColDef } from "ag-grid-community";

const Contracts: React.FC = () => {
    const { t } = useTranslation();

    const gridRef = useRef<any>(null);
    const [visualizationType, setVisualizationType] = useState<"grid" | "table">('grid');

    const { contractsApi } = useLoaderData() as { contractsApi: IContractApi[] | null };
    const [contracts, setContracts] = useState<IContractApi[]>([]);

    const canGo = useRef(true);
    useEffect(() => {
        if (canGo.current) {
            canGo.current = false;
            if (contractsApi !== null) {
                setContracts(contractsApi);
            }
        }
    }, []);

    const [isOpenModalPdfContracts, setIsOpenModalPdfContracts] = useState<boolean>(false);
    const [activeModalPdfContract, setActiveModalPdfContract] = useState<null | any>(null);

    // Sidebar per aggiunta documenti contratti
    const [isOpenSidebarContractDocuments, setIsOpenSidebarContractDocuments] = useState<boolean>(false);
    const [activeSidebarContractDocument, setActiveSidebarContractDocument] = useState<null | any>(null);

    const openCloseModalPdfContracts = (contract: any = null) => () => {
        if (isOpenModalPdfContracts === true) {
            setActiveModalPdfContract(null);
            setIsOpenModalPdfContracts(false);
        } else {
            setActiveModalPdfContract(contract);
            setIsOpenModalPdfContracts(true);
        }
    }

    const openCloseSidebarContractDocuments = (contractDocument: any = null) => {
        if (isOpenSidebarContractDocuments === true) {
            setActiveSidebarContractDocument(null);
            setIsOpenSidebarContractDocuments(false);
        } else {
            setActiveSidebarContractDocument(contractDocument);
            setIsOpenSidebarContractDocuments(true);
        }
    }

    const columnDefs = useMemo(() => [
        { field: 'name', headerName: t('screens.contracts.tableHeaders.contract') },
        { field: 'paymentTerms', headerName: t('screens.contracts.tableHeaders.paymentTerms') },
        {
            field: 'signedOn', headerName: t('screens.contracts.tableHeaders.signed'), cellRenderer: ({ data }: { data: any }) => {
                return (
                    <>
                        {
                            !!data.signedOn &&
                            <div>{dayjs(data.signedOn).format('DD/MM/YYYY')}</div>
                        }

                        {
                            !!!data.signedOn &&
                            <div>-</div>
                        }
                    </>
                )
            }
        },
        {
            field: 'validUntil', headerName: t('screens.contracts.tableHeaders.validUntil'), cellRenderer: ({ data }: { data: any }) => {
                return (
                    <>
                        {
                            !!data.validUntil &&
                            <div>{dayjs(data.validUntil).format('DD/MM/YYYY')}</div>
                        }

                        {
                            !!!data.validUntil &&
                            <div>-</div>
                        }
                    </>
                )
            }
        },
        {
            field: 'status', headerName: t('screens.contracts.tableHeaders.status'), cellRenderer: ({ data }: { data: any }) => {
                return (
                    <div className="flex items-center">
                        <div>
                            {
                                getContractsStatusIcon(data.status.id)
                            }
                        </div>
                        <div className="ml-1">{data.status.value}</div>
                    </div>
                )
            }
        }
    ], []);

    const onGridReady = () => {
        if (gridRef.current) {
            gridRef.current.api.setDomLayout('autoHeight');
        }
    }

    const [loadingAddendumAccept, setLoadingAddendumAccept] = useState(false);
    const acceptAddendum = async () => {
        if (!!activeModalPdfContract) {
            setLoadingAddendumAccept(true);
            const requestAccept = await contractsService.acceptAddendum(activeModalPdfContract.id);
            if (requestAccept.success) {
                const requestGetContracts = await contractsService.getContracts();
                if (requestGetContracts.success) {
                    setContracts(requestGetContracts.responseBody.contracts);
                    setIsOpenModalPdfContracts(false);
                } else {
                    window.location.reload();
                }
            } else {
                toast.error(t('errors.errorAcceptingAddendum'));
            }

            setLoadingAddendumAccept(false);
        }
    }



    const hasRedBg: (contract: IContractApi) => boolean = (contract) => {
        return (
            (!!contract.acceptanceRequested)
        );
    }

    const getRowClass = ({ data }: any) => {
        return hasRedBg(data) ? 'row-grid-red' : 'row-grid-white'
    }

    return (
        <div className="p-6 md:p-8">
            <SidebarAddContractDocument
                callbackCloseSidebar={() => openCloseSidebarContractDocuments()}
                isOpen={isOpenSidebarContractDocuments && activeSidebarContractDocument !== null}
                activeContractDocument={activeSidebarContractDocument}
                callbackUpdateContracts={(contracts) => {
                    setContracts(contracts);
                }}
            />





            {
                isOpenModalPdfContracts && activeModalPdfContract !== null &&
                <ModalPdfContracts
                    contract={activeModalPdfContract}
                    callbackCloseModal={openCloseModalPdfContracts()}
                    mustBeAccepted={activeModalPdfContract.mustBeAccepted === true}
                    callbackOnAccept={acceptAddendum}
                    loadingAcceptance={loadingAddendumAccept}
                />
            }

            <div className="md:flex items-start mb-6 md:mb-[30px]">
                <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[37.5px]">
                    {t('screens.contracts.heading')}
                </h1>
            </div>

            <div className="flex items-center justify-between md:mb-[26px]">
                {/* Grid or Table buttons */}
                <div className="hidden md:flex items-center">
                    <div
                        className={"mr-[10px] cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'grid' ? ' bg-red' : '')}
                        onClick={() => setVisualizationType('grid')}
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                            <path d="M14 8H8V14H14V8Z" fill={visualizationType === 'grid' ? "#FFFFFF" : "#6F6F6F"} />
                        </svg>
                    </div>

                    <div
                        className={"cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" + (visualizationType === 'table' ? ' bg-red' : '')}
                        onClick={() => setVisualizationType('table')}
                    >
                        <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0H16V2H0V0ZM0 4H16V6H0V4ZM0 8H16V10H0V8Z" fill={visualizationType === 'table' ? "#FFFFFF" : "#6F6F6F"} />
                        </svg>
                    </div>
                </div>
            </div>

            <div>
                {
                    visualizationType === 'grid' &&
                    <div className="grid grid-cols-12 gap-6">
                        {
                            contracts.length > 0 &&

                            contracts.map((contract, index) => {
                                return (
                                    <div
                                        className={"col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] shadow-card select-none p-4" + ((!!contract.acceptanceRequested) ? ' bg-[#FFEAED]' : ' bg-lightGrey')}
                                        key={index}
                                    >

                                        {/* Must be Accepted */}
                                        {
                                            !!contract.acceptanceRequested &&
                                            <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
                                                <div className="mr-1 grow">
                                                    <div className="text-center">
                                                        {t('screens.contracts.acceptanceRequested')}
                                                    </div>
                                                </div>
                                                <div className="relative">
                                                    <div className="cursor-pointer" id={`tooltip-contracts-acceptanceRequested-trigger-${index}`}>
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M18 9C18 13.879 13.879 18 9 18C4.121 18 0 13.879 0 9C0 4.121 4.121 0 9 0C13.879 0 18 4.121 18 9ZM16 9C16 5.206 12.794 2 9 2C5.206 2 2 5.206 2 9C2 12.794 5.206 16 9 16C12.794 16 16 12.794 16 9ZM10 10V5H8V10H10ZM10 13V11H8V13H10Z" fill="#CF0A2C" />
                                                        </svg>
                                                    </div>

                                                    <Tooltip
                                                        id={`tooltip-contracts-acceptanceRequested-${index}`}
                                                        triggerId={`tooltip-contracts-acceptanceRequested-trigger-${index}`}
                                                        title={t('screens.contracts.acceptanceRequested')}
                                                        description={t('screens.contracts.acceptanceRequestedText')}
                                                    />
                                                </div>
                                            </div>
                                        }

                                        <div className="text-black font-bold text-[22px] leading-[27px] mb-6">{contract.name}</div>

                                        {
                                            !!contract.contractDocuments && contract.contractDocuments.length > 0 &&
                                            <div>
                                                {
                                                    contract.contractDocuments.map((contractDocument, j) => {
                                                        return (
                                                            <div key={j} onClick={openCloseModalPdfContracts(contractDocument)} className={"flex items-center cursor-pointer mb-6" + (contractDocument.mustBeAccepted === true ? ' bg-[#FFEAED]' : '')}>
                                                                <div className="flex items-center overflow-hidden">
                                                                    <div className="mr-[6px]">
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <g clipPath="url(#clip0_12072_5168)">
                                                                                <path d="M22.5 21.75C22.5 22.1478 22.342 22.5294 22.0607 22.8107C21.7794 23.092 21.3978 23.25 21 23.25H3C2.60218 23.25 2.22064 23.092 1.93934 22.8107C1.65804 22.5294 1.5 22.1478 1.5 21.75V2.25C1.5 1.85218 1.65804 1.47064 1.93934 1.18934C2.22064 0.908036 2.60218 0.75 3 0.75H18.045C18.4365 0.749779 18.8127 0.902663 19.093 1.176L22.047 4.059C22.1904 4.19873 22.3043 4.36575 22.3821 4.5502C22.4599 4.73465 22.5 4.93281 22.5 5.133V21.75Z" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M6.04492 8.25H18.0449" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M6.04492 12.75H18.0449" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M6.04492 17.25H12.0449" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </g>
                                                                            <defs>
                                                                                <clipPath id="clip0_12072_5168">
                                                                                    <rect width="24" height="24" fill="white" />
                                                                                </clipPath>
                                                                            </defs>
                                                                        </svg>
                                                                    </div>
                                                                    <div className="text-sm text-red underline uppercase font-semibold leading-[17px]">{contractDocument.name}</div>
                                                                </div>

                                                                {
                                                                    contractDocument.mustBeAccepted === true &&
                                                                    <div className="shrink-0 uppercase text-white font-semibold text-[10px] bg-red rounded-full px-1 py-[3px]">{t('screens.contracts.actionRequired')}</div>
                                                                }
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        }

                                        {/* {
                                            !!contract.addendumDocuments && contract.addendumDocuments.length > 0 &&
                                            <div>
                                                {
                                                    contract.addendumDocuments.map((addendum, i: number) => {
                                                        return (
                                                            <div
                                                                onClick={openCloseModalPdfContracts(addendum)}
                                                                key={i}
                                                                className={"flex overflow-hidden items-center justify-between cursor-pointer mb-6" + (addendum.mustBeAccepted === true ? ' bg-[#FFEAED] p-[10px]' : '')}
                                                            >
                                                                <div className="flex items-center overflow-hidden">
                                                                    <div className="mr-[6px]">
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <g clipPath="url(#clip0_12072_5178)">
                                                                                <path d="M17.25 23.25C20.5637 23.25 23.25 20.5637 23.25 17.25C23.25 13.9363 20.5637 11.25 17.25 11.25C13.9363 11.25 11.25 13.9363 11.25 17.25C11.25 20.5637 13.9363 23.25 17.25 23.25Z" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M17.25 14.25V20.25" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M14.25 17.25H20.25" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M3.75 6.75H14.25" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M3.75 11.25H9.75" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M3.75 15.75H7.5" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                <path d="M7.5 20.25H2.25C1.85218 20.25 1.47064 20.092 1.18934 19.8107C0.908035 19.5294 0.75 19.1478 0.75 18.75V2.25C0.75 1.85218 0.908035 1.47064 1.18934 1.18934C1.47064 0.908035 1.85218 0.75 2.25 0.75H12.879C13.2765 0.750085 13.6578 0.907982 13.939 1.189L16.811 4.061C17.092 4.3422 17.2499 4.72345 17.25 5.121V7.5" stroke="#CF0A2C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </g>
                                                                            <defs>
                                                                                <clipPath id="clip0_12072_5178">
                                                                                    <rect width="24" height="24" fill="white" />
                                                                                </clipPath>
                                                                            </defs>
                                                                        </svg>

                                                                    </div>
                                                                    <div className="text-sm text-red underline uppercase font-semibold leading-[17px] overflow-hidden text-ellipsis">{addendum.name}</div>
                                                                </div>

                                                                {
                                                                    addendum.mustBeAccepted === true &&
                                                                    <div className="shrink-0 uppercase text-white font-semibold text-[10px] bg-red rounded-full px-1 py-[3px]">{t('screens.contracts.actionRequired')}</div>
                                                                }
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        } */}

                                        {
                                            !!contract.paymentTerms &&
                                            <div className="mb-[16px]">
                                                <div className="text-red uppercase leading-[17px] text-sm font-semibold">{t('screens.contracts.paymentTerms')}</div>
                                                <div className="text-lg leading-[22px]">{contract.paymentTerms}</div>
                                            </div>
                                        }

                                        {/* IBAN */}
                                        <div className="mb-[10px]">
                                            <div className="flex items-end mb-1">
                                                <div className="text-red uppercase leading-[17px] text-[16px] font-semibold">{t('screens.contracts.iban')}</div>
                                                {/*    {
                                                    contract.isIbanSwiftUnderEvaluation === false &&
                                                    <div onClick={() => openCloseSidebarContractDocuments(contract)} className="cursor-pointer ml-5 bg-red h-[22px] px-2 flex items-center justify-center rounded-[3px]">
                                                        <div className="flex items-center">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="h-full flex items-center cursor-pointer">
                                                                <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" className="fill-white" />
                                                                <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" className="fill-white" />
                                                            </svg>

                                                            <div className="ml-1 text-[12px] uppercase font-bold text-white leading-[10px]">
                                                                {t('screens.contracts.insertBankData')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                } */}

                                                {
                                                    contract.asideDocument &&
                                                    contract.asideDocument.id &&
                                                    contract.asideDocument.id.trim() !== '' &&
                                                    contract.isIbanSwiftUnderEvaluation === false &&
                                                    <div onClick={() => openCloseSidebarContractDocuments(contract.asideDocument)} className="cursor-pointer ml-2 bg-red h-[22px] px-2 flex items-center justify-center rounded-[3px]">
                                                        <div className="flex items-center">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="h-full flex items-center cursor-pointer">
                                                                <path d="M18.988 2.01196L21.988 5.01196L19.701 7.29996L16.701 4.29996L18.988 2.01196ZM8 16H11L18.287 8.71296L15.287 5.71296L8 13V16Z" className="fill-white" />
                                                                <path d="M19 19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.896 3 5V19C3 20.104 3.897 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.332L19 12.332V19Z" className="fill-white" />
                                                            </svg>

                                                            <div className="ml-1 text-[12px] uppercase font-bold text-white leading-[10px]">
                                                                {t('screens.contracts.insertBankData')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                }

                                                {
                                                    contract.isIbanSwiftUnderEvaluation === true &&
                                                    <div className="ml-1 text-[10px] uppercase font-bold text-white leading-[10px] bg-red px-3 py-1 rounded-full">
                                                        {t('screens.contracts.isUnderEvaluation')}
                                                    </div>
                                                }


                                            </div>

                                            {
                                                !!contract.bankName &&
                                                <div className="text-lg font-semibold leading-[22px]">{contract.bankName}</div>
                                            }
                                            <div className="leading-[19px] whitespace-break-spaces text-ellipsis overflow-hidden">{!!contract.iban ? contract.iban : '-'}</div>
                                        </div>

                                        {/* SWIFT */}
                                        {
                                            contract.countryISO !== "IT" &&
                                            <div className="mb-[10px]">
                                                <div className="text-red uppercase leading-[17px] text-[16px] font-semibold">{t('screens.contracts.swift')}</div>
                                                <div className="leading-[19px] whitespace-break-spaces text-ellipsis overflow-hidden">{!!contract.swift ? contract.swift : '-'}</div>
                                            </div>
                                        }



                                        <div className="bg-white p-4 text-sm leading-[17px] rounded-[4px]">
                                            {
                                                contract.acceptanceRequested === false &&
                                                <div className="text-left text-[13px] mb-[10px] leading-[16px]">
                                                    <div className="mb-[6px]">{t('screens.contracts.signed')} {!!contract.signedOn ? <span>{dayjs(contract.signedOn).format('DD/MM/YYYY')}</span> : <span>--</span>}</div>
                                                    <div>{!!contract.validUntil ? <strong> {t('screens.contracts.validUntil')} {dayjs(contract.validUntil).format('DD/MM/YYYY')}</strong> : <strong>--</strong>}</div>
                                                </div>
                                            }
                                            <div className={`flex items-center w-max px-1 rounded-[30px] text-white ${contract.status.id === 605530002 ? 'bg-[#EABD11]' : 'bg-green'}`}>
                                                <div>
                                                    {
                                                        getContractsStatusIcon(contract.status.id, 'white')
                                                    }
                                                </div>
                                                <div className="font-[500] text-[12px] uppercase leading-[14px] px-[4px] py-[3px]">{contract.status.value}</div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }

                        {
                            contracts.length === 0 &&
                            <div className="col-span-12">{t('screens.contracts.emptyContracts')}</div>
                        }
                    </div>
                }

                {
                    visualizationType === 'table' &&
                    <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={contracts} // Row Data for Rows 
                            columnDefs={columnDefs} // Column Defs for Columns 
                            defaultColDef={{
                                flex: 1,
                                minWidth: 100,
                            }}
                            onGridReady={onGridReady}
                            suppressRowClickSelection
                            suppressCellFocus
                            suppressMovableColumns
                            getRowClass={getRowClass}
                            rowStyle={{
                                overflow: 'visible'
                            }}
                            containerStyle={{
                                overflow: 'visible'
                            }}
                        />
                    </div>
                }
            </div>
        </div>
    )
}

export default Contracts;